import React, { useContext, useEffect, useState } from 'react';

import {
  transcriptionsAndSolutionsSendFeedback,
  transcriptionsAndSolutionsWarmup,
  transcriptionsAndSolutonsReset
} from 'sdk';

import { notifyErrors } from 'utils/notifications';

import ReviewAndConfirm from './components/ReviewAndConfirm';
import StepperAssignmentCreateForm from './components/StepperAssignmentCreateForm';
import AssignmentDialogContext from 'components/AssignmentDialogContext';
import Dialog from 'components/Dialog';
import UserContext from 'components/UserContext';

import { AssignAssignment, PreparingPage } from './components';

export const AssignmentDialogModes = {
  COPY: 'copy',
  UPDATE: 'update',
  CREATE: 'create',
  CREATE_FROM_TEMPLATE: 'create from template'
};

const AssignmentDialog = ({
  onClose,
  defaultTrackerId,
  assignmentTemplateId,
  isAcceleratedCurriculum,
  isMathAssignment = false,
  defaultStep = 0,
  defaultMode = AssignmentDialogModes.CREATE,
  isAssigned = false,
  bulkUploadId = null,
  hideBackButton = false
}) => {
  const [activeStep, setActiveStep] = useState(defaultStep);
  const [trackerId, setTrackerId] = useState(defaultTrackerId);

  useEffect(() => {
    transcriptionsAndSolutionsWarmup();
  }, []);

  const { setIsAssignmentDialogOpened, assignAssignmentSuccessAction } =
    useContext(AssignmentDialogContext);

  const { user } = useContext(UserContext);

  const [mode, setMode] = useState(defaultMode);
  const [selectedSections, setSelectedSections] = useState(null);

  const createFromTemplate =
    mode === AssignmentDialogModes.CREATE_FROM_TEMPLATE;

  const goToNextStep = () => setActiveStep(activeStep + 1);

  const onCreateSuccess = ({ id }) => {
    setTrackerId(id);
    goToNextStep();
  };

  const onCopySuccess = ({ id }) => {
    setTrackerId(id);
    setActiveStep(activeStep + 2);
  };

  const handleTranscriptionsAndSolutionsReset = async () => {
    if (trackerId) {
      const { success, errors } = await transcriptionsAndSolutonsReset({
        trackerId
      });
      if (!success) {
        notifyErrors(errors);
      }
    }
  };

  const renderStepCreateContent = () => {
    if (!createFromTemplate) {
      return (
        <StepperAssignmentCreateForm
          mode={mode}
          isMathAssignment={isMathAssignment}
          trackerId={trackerId}
          isAssigned={isAssigned}
          onClose={onClose}
          onCreateSuccess={onCreateSuccess}
          onUpdateSuccess={goToNextStep}
          onCopySuccess={onCopySuccess}
        />
      );
    } else {
      return (
        <StepperAssignmentCreateForm
          mode={mode}
          assignmentTemplateId={assignmentTemplateId}
          isAcceleratedCurriculum={isAcceleratedCurriculum}
          onCreateSuccess={onCreateSuccess}
          onClose={onClose}
          onCopySuccess={onCopySuccess}
        />
      );
    }
  };

  return (
    <Dialog
      open
      alignTop
      fullWidth
      maxWidth="md"
      onClose={onClose}
      disableBackdropClick={true}
    >
      {/* <ReviewAndConfirm  trackerId={33207}/> */}

      {activeStep === 0 && renderStepCreateContent()}
      {activeStep === 1 && isMathAssignment && (
        <PreparingPage
          onPrepareSuccess={goToNextStep}
          trackerId={trackerId}
          activeStep={activeStep}
        />
      )}
      {isMathAssignment &&
        activeStep === 2 &&
        mode !== AssignmentDialogModes.COPY && (
          <ReviewAndConfirm
            goToNextStep={() => {
              goToNextStep();
              transcriptionsAndSolutionsSendFeedback({ trackerId });
            }}
            goToBackStep={() => {
              setMode(AssignmentDialogModes.UPDATE);
              setActiveStep(activeStep - 2);
              handleTranscriptionsAndSolutionsReset();
            }}
            trackerId={trackerId}
            activeStep={activeStep}
          />
        )}
      {((activeStep === 2 && mode === AssignmentDialogModes.COPY) ||
        activeStep === 3 ||
        (!isMathAssignment && (activeStep === 1 || activeStep === 2))) &&
        trackerId && (
          <AssignAssignment
            trackerId={trackerId}
            onDialogClose={onClose}
            storeSelectedSections={setSelectedSections}
            selectedSections={selectedSections}
            prevStep={() => {
              if (mode === AssignmentDialogModes.COPY) {
                setMode(AssignmentDialogModes.UPDATE);
                const stepsBack = isMathAssignment ? 2 : 1;
                setActiveStep(activeStep - stepsBack);
                return;
              }
              setActiveStep(activeStep - 1);
            }}
            nextStep={() => setActiveStep(activeStep + 1)}
            onResultsClick={() => setIsAssignmentDialogOpened(false)}
            bulkUploadId={bulkUploadId}
            onAssignmentAssignSuccess={assignAssignmentSuccessAction}
            user={user}
            disableConfirmStep={
              mode === AssignmentDialogModes.COPY || !isMathAssignment
            }
            hideBackButton={hideBackButton}
          />
        )}
    </Dialog>
  );
};

export default AssignmentDialog;
